import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaExternalLinkAlt, FaGithub } from "react-icons/fa";
import styles from "../styles/ProjectDetail.module.css";

const projectDetails = {
  getthere: {
    title: "GetThere — Tour Aggregator Platform",
    roleNote: "Built at Sabrecwa · Production · Repo private",
    context:
      "When I joined the team, the company's tour operations ran through phone calls, WhatsApp messages, and a fragmented spreadsheet workflow. Operators would send tour details by email, pricing was manually calculated per request, and there was no single place where a traveler could browse, book, and pay. My job was to build that place.",
    story:
      "I designed and built GetThere from the ground up — a multi-operator tour platform where travel companies list packages, set per-distributor pricing rules, and accept payments, while travelers browse, book, and receive digital confirmations. I owned the full stack: schema design in MongoDB, the Node/Express API, the React frontend, Paystack integration with webhook-based payment confirmation, and Cloudinary for media. The white-label widget API came from a specific business need: partner sites wanted to sell our operators' packages without building a booking interface themselves. One script tag, fully configurable.",
    challenges: [
      "Pricing rules vary per distributor — a flat markup for one, percentage-based discount for another, plus time-limited overrides. I modelled this as a rule engine in MongoDB so ops could configure rules without a deploy.",
      "Paystack webhooks are not guaranteed to fire before the user's browser redirect. I handled confirmation server-side with idempotent webhook processing and a polling fallback on the client.",
      "Media uploads blocked the form UX early on. I moved Cloudinary uploads to a background queue and showed optimistic progress so the user could keep filling out the form.",
    ],
    highlights: [
      "Multi-operator platform live at getthere.sabrecwa.com",
      "Per-distributor pricing rule engine (markup, markdown, time-bound overrides)",
      "Paystack integration with idempotent webhook confirmation",
      "White-label widget API — one script tag, drop-in on any partner site",
      "Cloudinary background upload with optimistic UI feedback",
      "Role-based access: operators, admins, and partner-site distributors",
    ],
    live: "https://getthere.sabrecwa.com/",
    videoNote: "Walkthrough video coming soon",
  },
  "travelbook-lite": {
    title: "TravelBook Lite",
    context:
      "Working in travel tech means watching sub-agents at smaller agencies operate with zero tooling. They fund operations from personal bank accounts, manually email invoices, and have no visibility into what they're making on a booking. TravelBook Lite is what I built to fix that.",
    story:
      "TravelBook Lite is a B2B platform built for the sub-agent layer of the travel industry. A sub-agent logs in, searches flights, applies their own markup on top of the base fare, generates a branded PDF invoice for the client, and pays out of their virtual wallet. Wallet funding goes through Paystack for card/transfer or manual bank transfer — the parent agency's admin confirms bank transfers and authorises the wallet top-up. The admin side is where the parent agency lives: they see every sub-agent's activity, manage approval queues, and can lock or suspend wallets.",
    challenges: [
      "Wallet balance must be atomic — two concurrent bookings cannot both succeed if only one has enough balance. I used PostgreSQL transactions with row-level locking on the wallet record.",
      "Bank transfer confirmation is manual, so the flow needed a clear audit trail. Every state transition (pending → confirmed → funded) is logged with timestamps and the admin's ID.",
      "Invoice generation needed to work offline-first in poor network conditions — I render the invoice client-side with a PDF library and only hit the server to persist the record.",
    ],
    highlights: [
      "Virtual wallet per sub-agent with atomic balance operations (PostgreSQL row-level locks)",
      "Paystack card/transfer funding + manual bank transfer with admin approval queue",
      "Flight search with per-booking markup pricing applied before invoice",
      "Branded PDF invoice generation, rendered client-side",
      "Full admin panel: sub-agent management, wallet authorisation, transfer confirmation",
      "Immutable audit log for every wallet state transition",
    ],
    github: "https://github.com/Mercy-Iyanu/travelbook-lite",
  },
  "travel-search-ui": {
    title: "travel-search-ui",
    context:
      "Every travel project I've worked on starts the same way: rebuild a filter sidebar, a date range picker, and a results grid wired to some flight or tour API. I extracted the pattern into a package so I — and anyone else — wouldn't have to do it again.",
    story:
      "travel-search-ui is an open-source npm package that ships a composable Next.js/React travel search filter layout. You get a filter panel (origin, destination, dates, passengers, price range), a date range picker, and a results grid — all wired together and ready to connect to any flight or tour API. Zero config covers the common case; every component is overridable for custom APIs and brand styles. Written in TypeScript so IDE autocomplete actually helps you.",
    challenges: [
      "The package needs to work with any API shape — I designed the data layer around a transform function the consumer provides, keeping the components fully API-agnostic.",
      "Bundle size matters for a UI package. I kept dependencies minimal (no Moment.js, no heavy date libraries) and tree-shook everything that could be optional.",
      "TypeScript types for the filter state needed to be strict enough to catch mistakes but flexible enough not to force every consumer into one schema.",
    ],
    highlights: [
      "Zero-config filter panel, date picker, and results grid for travel search UIs",
      "Consumer-provided transform function — works with any flight or tour API",
      "Full TypeScript types with strict filter state schema",
      "Tree-shaken, minimal dependencies — no Moment.js",
      "Published on npm, source on GitHub",
    ],
    github: "https://github.com/Mercy-Iyanu/travel-search-ui",
    npm: "https://www.npmjs.com/package/travel-search-ui",
  },
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project = projectDetails[projectId];

  return (
    <div className={styles.page}>
      <button onClick={() => navigate(-1)} className={styles.backBtn}>
        <FaArrowLeft size={12} />
        Back to Projects
      </button>

      {project ? (
        <>
          <h1 className={styles.title}>{project.title}</h1>
          {project.roleNote && (
            <p className={styles.roleNote}>{project.roleNote}</p>
          )}

          <p className={styles.sectionLabel}>Background</p>
          <p className={styles.story}>{project.context}</p>

          <p className={styles.sectionLabel}>What I built</p>
          <p className={styles.story}>{project.story}</p>

          {project.challenges && (
            <>
              <p className={styles.sectionLabel}>Hard parts</p>
              <ul className={styles.highlights}>
                {project.challenges.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </>
          )}

          <p className={styles.sectionLabel}>Key highlights</p>
          <ul className={styles.highlights}>
            {project.highlights.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>

          {project.videoNote && (
            <p className={styles.videoNote}>{project.videoNote}</p>
          )}

          <div className={styles.actions}>
            {project.live && (
              <a
                href={project.live}
                target="_blank"
                rel="noreferrer"
                className={styles.liveBtnLink}
              >
                <FaExternalLinkAlt size={12} />
                Live Site
              </a>
            )}
            {project.github && (
              <a
                href={project.github}
                target="_blank"
                rel="noreferrer"
                className={styles.githubBtnLink}
              >
                <FaGithub size={15} />
                View Code
              </a>
            )}
            {project.npm && (
              <a
                href={project.npm}
                target="_blank"
                rel="noreferrer"
                className={styles.liveBtnLink}
              >
                <FaExternalLinkAlt size={12} />
                npm
              </a>
            )}
          </div>
        </>
      ) : (
        <p className={styles.notFound}>Project not found.</p>
      )}
    </div>
  );
};

export default ProjectDetail;
